import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, authedFileUrl } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Users, Bell, ImageIcon, FileText, Upload, ArrowRight, Loader2, UserCheck } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";

export default function AdminDashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

    const tiles = [
        { label: "Total Parents", value: stats.total_parents, icon: Users, color: "bg-secondary text-secondary-foreground" },
        { label: "Active Parents", value: stats.active_parents, icon: UserCheck, color: "bg-primary/20 text-primary-foreground" },
        { label: "Notifications", value: stats.total_notifications, icon: Bell, color: "bg-accent/20 text-accent-foreground dark:text-accent" },
        { label: "Photo Albums", value: stats.total_albums, icon: ImageIcon, color: "bg-secondary text-secondary-foreground" },
        { label: "Photos", value: stats.total_photos, icon: ImageIcon, color: "bg-primary/20 text-primary-foreground" },
        { label: "Documents", value: stats.total_documents, icon: FileText, color: "bg-accent/20 text-accent-foreground dark:text-accent" },
    ];

    const quickActions = [
        { to: "/admin/parents", label: "Manage Parents", icon: Users },
        { to: "/admin/bulk-upload", label: "Bulk Upload Parents", icon: Upload },
        { to: "/admin/notifications", label: "Send Notification", icon: Bell },
        { to: "/admin/photos", label: "Upload Photos", icon: ImageIcon },
        { to: "/admin/documents", label: "Upload Document", icon: FileText },
    ];

    return (
        <div className="space-y-8" data-testid="admin-dashboard">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Administrator Console</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-1">
                    Hello, <span className="text-secondary-foreground">{user?.username}</span>
                </h1>
                <p className="text-muted-foreground mt-2">A quick overview of the parent portal today.</p>
            </motion.div>

            {/* Stat tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {tiles.map((t, i) => (
                    <motion.div
                        key={t.label}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="bg-card border border-border rounded-2xl p-4"
                        data-testid={`stat-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        <div className={`h-10 w-10 rounded-2xl flex items-center justify-center ${t.color}`}>
                            <t.icon className="h-5 w-5" />
                        </div>
                        <div className="text-3xl font-black font-heading mt-3">{t.value}</div>
                        <div className="text-xs text-muted-foreground mt-1">{t.label}</div>
                    </motion.div>
                ))}
            </div>

            {/* Quick actions */}
            <section>
                <h2 className="font-heading text-xl font-bold mb-3">Quick Actions</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {quickActions.map((a) => (
                        <Link
                            key={a.to}
                            to={a.to}
                            data-testid={`quick-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="group flex items-center justify-between bg-card border border-border rounded-2xl p-4 hover:-translate-y-1 hover:shadow-md transition-all"
                        >
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-xl bg-primary/20 flex items-center justify-center">
                                    <a.icon className="h-5 w-5 text-primary-foreground" />
                                </div>
                                <div className="font-semibold">{a.label}</div>
                            </div>
                            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                        </Link>
                    ))}
                </div>
            </section>

            {/* Recent activity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="rounded-2xl">
                    <CardHeader>
                        <CardTitle className="font-heading">Recent Notifications</CardTitle>
                        <CardDescription>Latest announcements you&apos;ve sent</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {stats.recent_notifications.length === 0 && <div className="text-sm text-muted-foreground">None yet.</div>}
                        {stats.recent_notifications.map((n) => (
                            <div key={n.id} className="border-b border-border last:border-0 pb-2 last:pb-0">
                                <div className="font-semibold text-sm">{n.title}</div>
                                <div className="text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}</div>
                            </div>
                        ))}
                    </CardContent>
                </Card>

                <Card className="rounded-2xl">
                    <CardHeader>
                        <CardTitle className="font-heading">Recent Photos</CardTitle>
                        <CardDescription>Latest uploads</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {stats.recent_photos.length === 0 ? (
                            <div className="text-sm text-muted-foreground">No photos uploaded yet.</div>
                        ) : (
                            <div className="grid grid-cols-3 gap-2">
                                {stats.recent_photos.map((p) => (
                                    <div key={p.id} className="aspect-square rounded-xl overflow-hidden bg-muted">
                                        <img src={authedFileUrl(p.image_url)} alt={p.caption} className="w-full h-full object-cover" />
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
